import React from "react";
import { GEOSERVER_CONFIG, DEFAULT_WFS_PARAMS } from "../../../config/api";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  Paper,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  LocationOn as LocationOnIcon,
  ExpandLess,
  ExpandMore,
  AdsClick as AdsClickIcon,
} from "@mui/icons-material";
import PinDropIcon from "@mui/icons-material/PinDrop";

/** query tool drawer panel */
export default function QueryToolPanel(ctx) {
  const {
    queryLayer,
    setQueryLayer,
    queryClauses,
    setQueryClauses,
    invertWhere,
    setInvertWhere,
    queryLayerFields,
    setQueryLayerFields,
    queryFieldValues,
    setQueryFieldValues,
    queryFieldValuesLoading,
    setQueryFieldValuesLoading,
    fetchFieldValues,
    fetchLayerFields,
    handleApplyQuery,
    layerGroups,
    layerExtentsRef,
    layerNamesRef,
    wmsLayersRef,
    selectedQueryLayer,
    setSelectedQueryLayer,
    setAttributeTableOpen,
    setAttributeLoading,
    setAttributeColumns,
    setAttributeData,
    setAttributeLayerName,
    setAttributeTableDocked,
    setTablePage,
    setSelectedRows,
    attributeFeaturesRef,
    selectionLayerRef,
    selectedRowsRef,
    GEOSERVER_CONFIG,
    DEFAULT_WFS_PARAMS,
    onCloseTool,
  } = ctx;

  return (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                  >
                    Query Features
                  </Typography>

                  {/* Select Layer */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      Select Layer
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={queryLayer}
                      onChange={(e) => {
                        setQueryLayer(e.target.value);
                        fetchLayerFields(e.target.value);
                      }}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="">-- Select Layer --</MenuItem>
                      {Object.entries(layerGroups).map(([groupKey, group]) =>
                        Object.entries(group.children).map(
                          ([layerKey, layer]) => (
                            <MenuItem key={layerKey} value={layerKey}>
                              {layer.name}
                            </MenuItem>
                          ),
                        ),
                      )}
                    </Select>
                  </Box>

                  {/* Expression Builder */}
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      overflow: "visible",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        bgcolor: "#f0f4fa",
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, fontSize: 12 }}
                      >
                        Expression
                      </Typography>
                    </Box>

                    <Box sx={{ p: 1 }}>
                      {queryClauses.map((clause, idx) => (
                        <Box
                          key={clause.id}
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "center",
                            mb: 0.8,
                            flexWrap: "nowrap",
                          }}
                        >
                          {idx === 0 ? (
                            <Typography
                              variant="caption"
                              sx={{
                                width: 46,
                                fontSize: 12,
                                fontWeight: 500,
                                flexShrink: 0,
                              }}
                            >
                              Where
                            </Typography>
                          ) : (
                            <Select
                              size="small"
                              value={clause.conjunction || "And"}
                              onChange={(e) => {
                                const u = [...queryClauses];
                                u[idx].conjunction = e.target.value;
                                setQueryClauses(u);
                              }}
                              sx={{ width: 60, fontSize: 11 }}
                            >
                              <MenuItem value="And" sx={{ fontSize: 11 }}>
                                And
                              </MenuItem>
                              <MenuItem value="Or" sx={{ fontSize: 11 }}>
                                Or
                              </MenuItem>
                            </Select>
                          )}

                          {/* Field */}
                          <Select
                            size="small"
                            value={clause.field}
                            onChange={(e) => {
                              const u = [...queryClauses];
                              u[idx].field = e.target.value;
                              setQueryClauses(u);
                            }}
                            sx={{ flex: 1, fontSize: 11, minWidth: 0 }}
                            displayEmpty
                          >
                            <MenuItem
                              value=""
                              sx={{ fontSize: 11, color: "#aaa" }}
                            >
                              Field
                            </MenuItem>
                            {queryLayerFields.map((f) => (
                              <MenuItem key={f} value={f} sx={{ fontSize: 11 }}>
                                {f}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* Operator */}
                          <Select
                            size="small"
                            value={clause.operator}
                            onChange={(e) => {
                              const u = [...queryClauses];
                              u[idx].operator = e.target.value;
                              setQueryClauses(u);
                            }}
                            sx={{ flex: 1.2, fontSize: 11, minWidth: 0 }}
                          >
                            {[
                              "is equal to",
                              "is not equal to",
                              "is greater than",
                              "is less than",
                              "contains",
                              "starts with",
                              "is blank",
                              "is not blank",
                            ].map((op) => (
                              <MenuItem
                                key={op}
                                value={op}
                                sx={{ fontSize: 11 }}
                              >
                                {op}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* Value */}
                          {/* Value - Autocomplete with manual typing */}
                          <Box
                            sx={{ flex: 1, minWidth: 0, position: "relative" }}
                          >
                            <TextField
                              size="small"
                              placeholder="Value"
                              value={clause.value}
                              onChange={(e) => {
                                const u = [...queryClauses];
                                u[idx].value = e.target.value;
                                setQueryClauses(u);
                                // Filter suggestions
                                if (
                                  e.target.value.length > 0 &&
                                  queryFieldValues[clause.id]
                                ) {
                                  const u2 = [...queryClauses];
                                  u2[idx].showSuggestions = true;
                                  setQueryClauses(u2);
                                }
                              }}
                              onFocus={() => {
                                if (clause.field && queryLayer) {
                                  fetchFieldValues(
                                    queryLayer,
                                    clause.field,
                                    clause.id,
                                  );
                                }
                                const u = [...queryClauses];
                                u[idx].showSuggestions = true;
                                setQueryClauses(u);
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  const u = [...queryClauses];
                                  u[idx].showSuggestions = false;
                                  setQueryClauses(u);
                                }, 200);
                              }}
                              InputProps={{
                                endAdornment: queryFieldValuesLoading[
                                  clause.id
                                ] ? (
                                  <CircularProgress size={12} />
                                ) : null,
                              }}
                              sx={{
                                width: "100%",
                                "& input": { fontSize: 11 },
                              }}
                            />
                            {/* Suggestions Dropdown */}
                            {clause.showSuggestions &&
                              queryFieldValues[clause.id]?.length > 0 && (
                                <Paper
                                  sx={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: 150,
                                    overflow: "auto",
                                    zIndex: 99999,
                                    border: "1px solid #1976d2",
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                                    bgcolor: "white",
                                  }}
                                >
                                  {queryFieldValues[clause.id]
                                    .filter(
                                      (v) =>
                                        clause.value === "" ||
                                        v
                                          .toLowerCase()
                                          .includes(clause.value.toLowerCase()),
                                    )
                                    .map((val, vi) => (
                                      <Box
                                        key={vi}
                                        onMouseDown={() => {
                                          const u = [...queryClauses];
                                          u[idx].value = val;
                                          u[idx].showSuggestions = false;
                                          setQueryClauses(u);
                                        }}
                                        sx={{
                                          px: 1.5,
                                          py: 0.6,
                                          fontSize: 11,
                                          cursor: "pointer",
                                          "&:hover": { bgcolor: "#e3f2fd" },
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                      >
                                        {val}
                                      </Box>
                                    ))}
                                </Paper>
                              )}
                          </Box>

                          {/* Remove */}
                          <IconButton
                            size="small"
                            onClick={() =>
                              queryClauses.length > 1 &&
                              setQueryClauses(
                                queryClauses.filter((c) => c.id !== clause.id),
                              )
                            }
                            disabled={queryClauses.length === 1}
                            sx={{ color: "red", p: 0.3, flexShrink: 0 }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}

                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                        onClick={() =>
                          setQueryClauses([
                            ...queryClauses,
                            {
                              id: Date.now(),
                              field: "",
                              operator: "is equal to",
                              value: "",
                              conjunction: "And",
                            },
                          ])
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: 11,
                          color: "#1976d2",
                          mt: 0.5,
                          px: 0,
                        }}
                      >
                        Add Clause
                      </Button>
                    </Box>
                  </Box>

                  {/* Invert Where Clause */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Checkbox
                      size="small"
                      checked={invertWhere}
                      onChange={(e) => setInvertWhere(e.target.checked)}
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      Invert Where Clause
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flexShrink: 0,
                      mb: 2,
                      mt: "auto",
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleApplyQuery}
                      sx={{
                        bgcolor: "#1976d2",
                        "&:hover": { bgcolor: "#115293" },
                      }}
                    >
                      Apply
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setQueryLayer("");
                          setQueryClauses([
                            {
                              id: Date.now(),
                              field: "",
                              operator: "is equal to",
                              value: "",
                            },
                          ]);
                          setInvertWhere(false);
                          setQueryLayerFields([]);
                          setSelectedRows([]);
                          if (selectionLayerRef.current) {
                            selectionLayerRef.current.getSource().clear();
                            selectionLayerRef.current.changed();
                          }
                        }}
                        sx={{ borderColor: "#ff9800", color: "#ff9800" }}
                      >
                        Clear
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          onCloseTool("query");
                          setQueryLayer("");
                          setQueryClauses([
                            {
                              id: Date.now(),
                              field: "",
                              operator: "is equal to",
                              value: "",
                            },
                          ]);
                          setInvertWhere(false);
                          setQueryLayerFields([]);
                        }}
                        sx={{
                          borderColor: "#f44336",
                          color: "#f44336",
                          "&:hover": {
                            bgcolor: "#ffebee",
                            borderColor: "#f44336",
                          },
                        }}
                      >
                        Close
                      </Button>
                    </Box>
                  </Box>
                </Box>
  );
}
